/**
 * Pieces of the guest hero visual: the frame (a visually hidden description for screen readers,
 * the decorative art hidden from them) and a game icon with the Browse tile's bezel.
 */
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { cx } from '@/components/ui';
import type { HeroGame } from './types';
import './guestHero.css';

const FALLBACK_ICON = '/icons/egame.jpg';

/** The visually hidden sentence that stands in for the art: "Featured games: A, B, C". */
export function useHeroDescription(games: readonly HeroGame[]): string {
  const { t } = useTranslation();
  const names = Array.from(new Set(games.map((game) => game.name)));
  return t('home.guestHero.description', 'Featured games: {{games}}', { games: names.join(', ') });
}

export interface HeroFrameProps {
  games: readonly HeroGame[];
  still: boolean;
  className?: string;
  children: React.ReactNode;
}

/**
 * Root of the visual. Motion classes only apply under `.gh-live`, which a still hero never gets,
 * so a still hero renders the same markup with nothing moving.
 */
export function HeroFrame({ games, still, className, children }: HeroFrameProps) {
  const description = useHeroDescription(games);
  return (
    <div className={cx('relative h-full w-full', !still && 'gh-live')}>
      <p className="sr-only">{description}</p>
      <div aria-hidden="true" data-testid="guest-hero-art" className={cx('gh-art absolute inset-0', className)}>
        {children}
      </div>
    </div>
  );
}

export interface HeroIconProps {
  game: HeroGame;
  className?: string;
  /** Show the "Hot" corner tag when the game is hot (one icon per hero at most). */
  showHot?: boolean;
}

/** A game icon: rounded art inside the tile bezel; decorative (alt=""), the frame describes it. */
export function HeroIcon({ game, className, showHot = false }: HeroIconProps) {
  const { t } = useTranslation();
  const [failedIcon, setFailedIcon] = useState<string | null>(null);
  const src = failedIcon === game.icon ? FALLBACK_ICON : game.icon;
  return (
    <div className={cx('gh-icon lobby-gi-ico relative isolate overflow-hidden', className)} data-hero-game={game.id}>
      <img
        src={src}
        alt=""
        draggable={false}
        decoding="async"
        onError={() => {
          if (src !== FALLBACK_ICON) setFailedIcon(game.icon);
        }}
      />
      {showHot && game.hot ? (
        <span className="game-tile-hot z-10 bg-action-danger font-ui text-on-danger">{t('home.guestHero.hot', 'Hot')}</span>
      ) : null}
    </div>
  );
}
